import { Card, Typography, Form, Input, InputNumber, Select, Button, Row, Col, Space, Tag, AutoComplete, DatePicker, Upload, Divider, Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, InfoCircleOutlined, PlusOutlined, DeleteOutlined,
    ScanOutlined, InboxOutlined, FileTextOutlined, ClearOutlined,
} from "@ant-design/icons"
import { PurchaseOrderService } from '../../../Services/Manage/PurchaseOrderService'
import { ENDPOINT } from '../../../Services/ENDPOINT'
import { SupplierService } from '../../../Services/Manage/SupplierService'
import { ItemService } from '../../../Services/Manage/ItemService'
import { ProjectService } from '../../../Services/Manage/ProjectService'
import { UserService } from '../../../Services/Manage/UserService'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
  .po-equal-height .ant-upload-drag { height: 132px; }
  .po-equal-height textarea.ant-input { height: 132px !important; resize: none; }
`;

const CardTitle = ({ icon, no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const gstOptions = [0, 5, 12, 18, 28].map((v) => ({ value: v, label: `${v}% GST` }));

const AddPurchaseOrder = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [poNumber, setPoNumber] = useState("PO-2024-000001");
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [allSuppliers, setAllSuppliers] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [itemSearch, setItemSearch] = useState("");
    const [projectOptions, setProjectOptions] = useState([]);
    const [userMap, setUserMap] = useState({});

    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(1);


    const [scanModalOpen, setScanModalOpen] = useState(false);
    const [scanValue, setScanValue] = useState("");

    const [editPO, setEditPO] = useState(null);
    const [saving, setSaving] = useState(false);
    const [documentFiles, setDocumentFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    const uploadOneFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${ENDPOINT.PurchaseOrder}/upload-file`, { method: "POST", body: formData });
        const result = await res.json();
        return result?.status && result?.success ? result.file : null;
    };

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;
    const id = location.state?.id;
    const action = location.state?.action;
    const enableEdit = location.state?.enableEdit;
    const from = location.state?.from || '/inventory/purchase-orders';
    const isReadOnly = !enableEdit && !!editPO;

    useEffect(() => {
        getAllSuppliers();
        getAllItems();
        getAllProjects();
        getUserMap();
        if (id) {
            getPOById(id);
        } else {
            getLastPO();
        }
    }, [id]);

    const getUserMap = async () => {
        try {
            const request = await UserService.getAllUsers();
            const map = {};
            (request.data || []).forEach((u) => {
                if (u.user_id) map[String(u.user_id)] = u.name;
            });
            setUserMap(map);
        } catch (error) { console.error(error); }
    };
    const getAllSuppliers = async () => {
        try {
            const request = await SupplierService.getAllSupplier();
            const data = request.data || [];
            setAllSuppliers(data);
            setSupplierOptions(data.map((s) => ({ value: s.id, label: s.supplier_name })));
        } catch (error) { console.error(error); }
    };

    const getAllItems = async () => {
        try {
            const request = await ItemService.getAllItem();
            const active = (request.data || []).filter((i) => i.is_active);
            setAllItems(active);
        } catch (error) { console.error(error); }
    };

    const getAllProjects = async () => {
        try {
            const request = await ProjectService.getAllProject();
            const data = request.data || [];
            setProjectOptions(
                data.map((p) => ({
                    value: p.pro_name,
                    label: p.pro_no ? `${p.pro_name} (${p.pro_no})` : p.pro_name,
                }))
            );
        } catch (error) { console.error(error); }
    };

    const getLastPO = async () => {
        try {
            const request = await PurchaseOrderService.getLastPO();
            let nextNo = "000001";
            if (request?.status && request?.data?.po_number) {
                const seqPart = request.data.po_number.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) nextNo = String(parsed + 1).padStart(6, "0");
            }
            setPoNumber(`PO-${dayjs().format("YYYY")}-${nextNo}`);
        } catch (error) { console.error(error); }
    };

    const getPOById = async (id) => {
        try {
            const request = await PurchaseOrderService.getPOById(id);
            const initialValues = request.data;
            setEditPO(initialValues);
            setPoNumber(initialValues.po_number);
            setRows((initialValues.items || []).map((it, idx) => ({ ...it, key: idx })));
            setRowCount((initialValues.items || []).length + 1);

            let parsedAttachments = [];
            try {
                parsedAttachments = initialValues.attachments
                    ? (typeof initialValues.attachments === "string"
                        ? JSON.parse(initialValues.attachments)
                        : initialValues.attachments)
                    : [];
            } catch { parsedAttachments = []; }

            setExistingAttachments(
                (parsedAttachments || []).map((f, idx) => ({
                    uid: f.uid || `existing-${idx}`,
                    name: f.name || f.filename || `file-${idx}`,
                    status: "done",
                    url: `${ENDPOINT.base}/${f.path || f.url}`,
                    ...f,
                }))
            );

            form.setFieldsValue({
                ...initialValues,
                po_date: initialValues.po_date ? dayjs(initialValues.po_date) : null,
                required_date: initialValues.required_date ? dayjs(initialValues.required_date) : null,
                delivery_date: initialValues.delivery_date ? dayjs(initialValues.delivery_date) : null,
            });
        } catch (error) { console.error(error); }
    };

    const onSupplierChange = (supplierId) => {
        const s = allSuppliers.find((x) => Number(x.id) === Number(supplierId));
        if (!s) return;
        form.setFieldsValue({
            supplier_gstin: s.gstin_tax_id || "",
            billing_address: s.billing_address || "",
            shipping_address: s.shipping_address || "",
            payment_terms: s.payment_terms || "",
        });
    };

    const calcRowAmount = (row) => {
        const qty = Number(row.qty || 0);
        const rate = Number(row.rate || 0);
        const discountPercent = Number(row.discount_percent || 0);
        const taxPercent = Number(row.tax_percent || 0);
        const base = qty * rate;
        const discountAmt = (base * discountPercent) / 100;
        const taxable = base - discountAmt;
        const taxAmt = (taxable * taxPercent) / 100;
        return Math.round(taxable + taxAmt);
    };

    const handleRowChange = (key, field, value) => {
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            const updated = { ...row, [field]: value };
            updated.amount = calcRowAmount(updated);
            return updated;
        }));
    };

    const handleSelectItem = (key, itemId) => {
        const selected = allItems.find((x) => Number(x.id) === Number(itemId));
        if (!selected) return;
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            const updated = {
                ...row,
                item_id: selected.id,
                item_name: selected.item_name,
                description: selected.description || "",
                hsn_sku: selected.hsn_sac_code || selected.sku_barcode || "",
                unit: selected.unit || "",
                rate: Number(selected.cost_price || 0),
                tax_percent: Number(selected.tax_rate || 0),
            };
            updated.amount = calcRowAmount(updated);
            return updated;
        }));
        setItemSearch("");
    };

    const handleBarcodeScanned = (rawValue) => {
        const code = (rawValue || "").trim();
        if (!code) {
            messageApi.warning("Please enter a barcode number");
            return;
        }

        const matched = allItems.find((i) => String(i.barcode || i.sku_barcode).trim() === code);

        if (!matched) {
            messageApi.warning(`No item found for barcode: ${code}`);
            return;
        }

        const emptyRow = rows.find((r) => !r.item_id);
        if (emptyRow) {
            handleSelectItem(emptyRow.key, matched.id);
        } else {
            const newKey = rowCount;
            setRows((prev) => [...prev, {
                key: newKey,
                item_id: matched.id,
                item_name: matched.item_name,
                description: matched.description || "",
                hsn_sku: matched.hsn_sac_code || matched.sku_barcode || "",
                qty: 1,
                unit: matched.unit || "",
                rate: Number(matched.cost_price || 0),
                discount_percent: 0,
                tax_percent: Number(matched.tax_rate || 0),
                amount: calcRowAmount({ qty: 1, rate: Number(matched.cost_price || 0), discount_percent: 0, tax_percent: Number(matched.tax_rate || 0) }),
            }]);
            setRowCount(newKey + 1);
        }

        messageApi.success(`Added: ${matched.item_name}`);
        setScanValue("");
    };

    const handleAddRow = () => {
        setRows((prev) => [...prev, {
            key: rowCount, item_id: null, item_name: "", description: "", hsn_sku: "",
            qty: 1, unit: "", rate: 0, discount_percent: 0, tax_percent: 0, amount: 0,
        }]);
        setRowCount(rowCount + 1);
    };

    const handleDeleteRow = (key) => {
        setRows((prev) => prev.filter((r) => r.key !== key));
    };

const handleClearRow = (key) => {
    setRows((prev) => prev.map((row) => {
        if (row.key !== key) return row;
        return {
            key: row.key, item_id: null, item_name: "", description: "", hsn_sku: "",
            qty: 1, unit: "", rate: 0, discount_percent: 0, tax_percent: 0, amount: 0,
        };
    }));
    setItemSearch("");
};

    const itemAutoOptions = allItems
        .filter((i) => !itemSearch || i.item_name.toLowerCase().includes(itemSearch.toLowerCase()))
        .map((i) => ({ value: String(i.id), label: `${i.item_name}${i.sku_barcode ? ` (${i.sku_barcode})` : ""}` }));

    const subTotal = rows.reduce((sum, r) => sum + Number(r.qty || 0) * Number(r.rate || 0), 0);
    const totalDiscount = rows.reduce((sum, r) => sum + (Number(r.qty || 0) * Number(r.rate || 0) * Number(r.discount_percent || 0)) / 100, 0);
    const taxableAmount = subTotal - totalDiscount;
    const totalGst = rows.reduce((sum, r) => {
        const base = Number(r.qty || 0) * Number(r.rate || 0);
        const discountAmt = (base * Number(r.discount_percent || 0)) / 100;
        const taxable = base - discountAmt;
        return sum + (taxable * Number(r.tax_percent || 0)) / 100;
    }, 0);
    const totalAmount = Math.round(taxableAmount + totalGst);
    const totalQty = rows.reduce((sum, r) => sum + Number(r.qty || 0), 0);

    const onFinish = async (values) => {
        await performSave(values);
    };

    const handleSaveDraft = async () => {
        const values = form.getFieldsValue();
        await performSave(values);
    };

    const performSave = async (values) => {
        if (!rows.length) {
            messageApi.warning("Please add at least one item");
            return;
        }
        setSaving(true);
        try {
            let uploadedAttachments = [...existingAttachments];
            for (const f of documentFiles) {
                const uploaded = await uploadOneFile(f);
                if (uploaded) uploadedAttachments.push(uploaded);
            }

            const selectedSupplier = allSuppliers.find((x) => Number(x.id) === Number(values.supplier_id));
            const payload = {
                attachments: JSON.stringify(uploadedAttachments),
                ...values,
                po_number: poNumber,
                po_date: values.po_date ? dayjs(values.po_date).format("YYYY-MM-DD") : null,
                required_date: values.required_date ? dayjs(values.required_date).format("YYYY-MM-DD") : null,
                delivery_date: values.delivery_date ? dayjs(values.delivery_date).format("YYYY-MM-DD") : null,
                supplier_name: selectedSupplier?.supplier_name || editPO?.supplier_name || "",
                sub_total: subTotal,
                discount: totalDiscount,
                taxable_amount: taxableAmount,
                total_gst: totalGst,
                total_amount: totalAmount,
                status: editPO?.status || "Pending",
                added_by: editPO ? editPO.added_by : currentUserId,
                items: rows.map(({ key, ...rest }) => rest),
            };

            let request;
            if (editPO) {
                request = await PurchaseOrderService.updatePO(payload, id);
            } else {
                request = await PurchaseOrderService.createPO(payload);
            }

            if (request?.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error in onFinish:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            const request = await PurchaseOrderService.deletePO(id);
            if (request.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request.message);
            }
        } catch (error) { console.error(error); }
    };

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined style={{ cursor: "pointer", fontSize: "20px" }} onClick={() => navigate(from)} />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {editPO ? `${action} Purchase Order` : 'New Purchase Order'}{' '}
                                    {editPO && (
                                        <Tag color="cyan" style={{ fontSize: '16px', padding: '6px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            {editPO.po_number}
                                        </Tag>
                                    )}
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    action === 'View' ? null : !enableEdit && editPO ? (
                        <Button size="large" danger onClick={() => navigate("/inventory/purchase-orders/edit-purchase-order", { state: { id, action: 'Edit', enableEdit: true, from } })}>
                            Enable Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button size="large" loading={saving} onClick={handleSaveDraft}>Save as Draft</Button>
                            <Button
                                type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                            >
                                {editPO ? "Update Purchase Order" : "Save Purchase Order"}
                            </Button>
                        </Space>
                    )
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form layout="vertical" form={form} onFinish={onFinish} size="middle" disabled={isReadOnly}>
                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={1} text="Purchase Order Details" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="PO Number">
                                            <Input size="large" readOnly value={poNumber} placeholder="Auto generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="po_date" label="PO Date">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="required_date" label="Required Date">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="delivery_date" label="Delivery Date">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true, message: 'Please select supplier' }]}>
                                            <Select options={supplierOptions} size="large" showSearch allowClear placeholder="Select supplier"
                                                onChange={onSupplierChange}
                                                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="supplier_gstin" label="Supplier GSTIN / Tax ID">
                                            <Input size="large" placeholder="Enter GSTIN / Tax ID" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="currency" label="Currency">
                                            <Input size="large" placeholder="Enter currency (e.g. INR, USD)" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="exchange_rate" label="Exchange Rate">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="1.0000" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item name="billing_address" label="Billing Address">
                                            <TextArea rows={2} placeholder="Enter billing address" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="shipping_address" label="Shipping Address">
                                            <TextArea rows={2} placeholder="Enter shipping address" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="payment_terms" label="Payment Terms">
                                            <Input size="large" placeholder="Enter payment terms" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="credit_days" label="Credit Days">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="30" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="project_name" label="Project (Optional)">
                                            <Select
                                                options={projectOptions}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select project"
                                                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reference_remarks" label="Reference / Remarks">
                                            <Input size="large" placeholder="Enter reference or remarks" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={2} text="Items" />}>
                                <Space style={{ marginBottom: 12 }}>
                                    <Button icon={<PlusOutlined />} type="primary" onClick={handleAddRow}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                                        Add Item
                                    </Button>
                                    <Button icon={<ScanOutlined />} onClick={() => setScanModalOpen(true)}>Scan Barcode</Button>                                </Space>

                                {rows.length > 0 && (
                                    <Row gutter={8} style={{ padding: "0 8px", marginBottom: 6 }}>
                                        <Col xs={0} md={1}><Text type="secondary" strong style={{ fontSize: 12 }}>#</Text></Col>
                                        <Col xs={24} md={5}><Text type="secondary" strong style={{ fontSize: 12 }}>Item / Product</Text></Col>
                                        <Col xs={12} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>HSN/SKU</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Qty</Text></Col>
                                        <Col xs={6} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>Rate (₹)</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Discount (%)</Text></Col>
                                        <Col xs={6} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>Tax (%)</Text></Col>
                                        <Col xs={18} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>Amount (₹)</Text></Col>
                                        <Col xs={6} md={2}></Col>
                                    </Row>
                                )}

                                {rows.map((row, idx) => (
                                    <Row key={row.key} gutter={8} align="middle" style={{ marginBottom: 8, border: "1px solid #f0f0f0", borderRadius: 8, padding: 8 }}>
                                        <Col xs={0} md={1}>
                                            <Text type="secondary">{idx + 1}</Text>
                                        </Col>
                                        <Col xs={24} md={5}>
                                            {/* <AutoComplete
                                                style={{ width: "100%" }}
                                                options={itemAutoOptions}
                                                value={row.item_name}
                                                placeholder="Type item / product"
                                                filterOption={false}
                                                allowClear
                                                onSearch={(val) => setItemSearch(val)}
                                                onSelect={(val) => handleSelectItem(row.key, val)}
                                                onClear={() => handleClearRow(row.key)}
                                                onChange={(val) => {
                                                    if (!val) {
                                                        handleClearRow(row.key);
                                                    } else {
                                                        handleRowChange(row.key, "item_name", val);
                                                    }
                                                }}
                                            /> */}

                                            <AutoComplete
    style={{ width: "100%" }}
    options={itemAutoOptions}
    value={row.item_name}
    placeholder="Search item / product"
    filterOption={false}
    allowClear
    notFoundContent="No matched items / products"
    onSearch={(val) => {
        setItemSearch(val);
        handleRowChange(row.key, "item_name", val);
    }}
    onSelect={(val) => handleSelectItem(row.key, val)}
    onClear={() => handleClearRow(row.key)}
    onBlur={() => {
       
        if (!row.item_id) {
            handleClearRow(row.key);
        }
    }}
/>
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <Input size="middle" placeholder="HSN/SKU" value={row.hsn_sku}
                                                onChange={(e) => handleRowChange(row.key, "hsn_sku", e.target.value)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <InputNumber min={0} style={{ width: '100%' }} placeholder="Qty" value={row.qty}
                                                onChange={(v) => handleRowChange(row.key, "qty", v)} />
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <InputNumber min={0} style={{ width: '100%' }} placeholder="Rate" value={row.rate}
                                                onChange={(v) => handleRowChange(row.key, "rate", v)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="Disc %" value={row.discount_percent}
                                                onChange={(v) => handleRowChange(row.key, "discount_percent", v)} />
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Select options={gstOptions} style={{ width: '100%' }} placeholder="Tax" value={row.tax_percent}
                                                onChange={(v) => handleRowChange(row.key, "tax_percent", v)} />
                                        </Col>
                                        <Col xs={18} md={3}>
                                            <Text strong>₹ {Number(row.amount || 0).toLocaleString('en-IN')}</Text>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Button danger type="text" icon={<DeleteOutlined />} title="Delete row" onClick={() => handleDeleteRow(row.key)} />
                                        </Col>
                                    </Row>
                                ))}

                                {/* {rows.length === 0 && (
                                    <Text type="secondary">Add items to this purchase order</Text>
                                )} */}
                            </Card>
                            <Card title={<CardTitle icon={<FileTextOutlined />} no={3} text="Additional Information" />} className="po-equal-height">
                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <Form.Item label="Attachments">
                                            <Upload.Dragger
                                                multiple
                                                beforeUpload={(file) => { setDocumentFiles((prev) => [...prev, file]); return false; }}
                                                onRemove={(file) => {
                                                    if (file.status === "done") {
                                                        setExistingAttachments((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    } else {
                                                        setDocumentFiles((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    }
                                                }}
                                                fileList={[...existingAttachments, ...documentFiles]}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click or drag files to upload</p>
                                                <p className="ant-upload-hint">PNG, JPG, PDF (Max. 5MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="terms_conditions" label="Terms & Conditions">
                                            <TextArea maxLength={500} showCount placeholder="Enter terms and conditions..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="notes" label="Notes">
                                            <TextArea maxLength={500} showCount placeholder="Enter notes..." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>
                        {!isReadOnly && (
                            <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
                                <Space>
                                    <Button size='large' onClick={() => { form.resetFields(); setRows([]); }}>Reset</Button>
                                    {editPO && (
                                        <Button size="large" danger onClick={handleDelete}>Delete PO</Button>
                                    )}
                                    <Button
                                        type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        {editPO ? "Update Purchase Order" : "Save Purchase Order"}
                                    </Button>
                                </Space>
                            </div>
                        )}

                        {editPO && (
                            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#999' }}>
                                Created by: {userMap[String(editPO.added_by)] || editPO.added_by || '-'}
                                {'  |  '}
                                Last updated: {editPO.updated_at ? dayjs(editPO.updated_at).format('DD-MM-YYYY HH:mm') : '-'}
                            </div>
                        )}
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Order Summary</span>} size="small">
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Sub Total", `₹ ${subTotal.toLocaleString('en-IN')}`],
                                    ["Discount", `- ₹ ${totalDiscount.toLocaleString('en-IN')}`],
                                    ["Taxable Amount", `₹ ${taxableAmount.toLocaleString('en-IN')}`],
                                    ["Total GST", `₹ ${totalGst.toLocaleString('en-IN')}`],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                                <Divider style={{ margin: "6px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
                                    <Text strong>Total Amount</Text>
                                    <Text strong style={{ color: "rgba(14, 93, 146, 0.95)" }}>₹ {totalAmount.toLocaleString('en-IN')}</Text>
                                </div>
                            </Space>
                        </Card>

                        <Card title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Summary Detail</span>} size="small">
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Total Items", rows.length],
                                    ["Total Quantity", totalQty],
                                    ["Total Tax", `₹ ${totalGst.toLocaleString('en-IN')}`],
                                    ["Total Discount", `₹ ${totalDiscount.toLocaleString('en-IN')}`],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>

                <Modal
                    title="Enter Barcode"
                    open={scanModalOpen}
                    onCancel={() => { setScanModalOpen(false); setScanValue(""); }}
                    footer={null}
                    destroyOnClose
                >
                    <Space.Compact style={{ width: "100%" }}>
                        <Input
                            autoFocus
                            size="large"
                            placeholder="Enter barcode number..."
                            value={scanValue}
                            onChange={(e) => setScanValue(e.target.value)}
                            onPressEnter={() => handleBarcodeScanned(scanValue)}
                        />
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => handleBarcodeScanned(scanValue)}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            Enter
                        </Button>
                    </Space.Compact>
                </Modal>

            </Card>
        </>
    );
};

export default AddPurchaseOrder;